import type { DomainEventDefinition, JsonValue, RecordDefinition, Transaction } from "@e308/core";
import { settleCombat } from "./combat.js";
import { cloneCombatState, settleShields } from "./combat-state.js";
import type { CombatSeatInput } from "./combat-types.js";
import type { SeatModel, ShieldEventPayload } from "./content-types.js";
import { pactBlocksCombat } from "./diplomacy-state.js";
import type { DiplomacyState } from "./diplomacy-types.js";
import { ECONOMY_BUILDINGS, type EconomySettlement } from "./economy-types.js";
import { formatMicro, type Micro, microFromString } from "./exact-numbers.js";
import { borderCanFight, type LockedMatchSetup } from "./lifecycle-state.js";
import type { MatchLifecycleState } from "./lifecycle-types.js";
import { cloneLogisticsState } from "./logistics-clone.js";
import { supplyForPosition } from "./logistics-state.js";
import type { LogisticsState } from "./logistics-types.js";
import { effectsForResearch } from "./research-effects.js";
import { modelBuildingResource } from "./seat-resources.js";

export interface CapturedSettlement {
  readonly model: SeatModel;
  readonly result: EconomySettlement;
}

export function settleBorderShields(
  transaction: Transaction<Micro>,
  settlements: readonly CapturedSettlement[],
): void {
  for (const { model, result } of settlements) {
    const allocations = Object.fromEntries(
      model.borderIds.map((id) => [
        id,
        Number(transaction.getAllocation(model.shieldAllocation.id, id) / 1_000_000n),
      ]),
    );
    transaction.setRecord(
      model.combat,
      settleShields(
        transaction.getRecord(model.combat),
        formatMicro(result.diagnostics.shieldCapacity),
        formatMicro(result.diagnostics.shieldRecharge),
        allocations,
      ),
    );
  }
}

export function runCombatStep(
  transaction: Transaction<Micro>,
  settlements: readonly CapturedSettlement[],
  elapsedMs: number,
  lifecycleDefinition: RecordDefinition<MatchLifecycleState & JsonValue>,
  diplomacyDefinition: RecordDefinition<DiplomacyState & JsonValue>,
  setup: LockedMatchSetup,
  shieldBreached: DomainEventDefinition<ShieldEventPayload>,
): readonly import("./combat-types.js").CombatDamageReport[] {
  const lifecycle = transaction.getRecord(lifecycleDefinition);
  const diplomacy = transaction.getRecord(diplomacyDefinition);
  const capture = settlements.map(({ model, result }) =>
    captureSeat(transaction, model, result, lifecycle, diplomacy, setup, elapsedMs),
  );
  const settled = settleCombat(capture, elapsedMs);
  const resultById = new Map(settled.seats.map((seat) => [seat.id, seat]));
  for (const { model } of settlements) {
    const result = resultById.get(model.seat.id) as (typeof settled.seats)[number];
    transaction.set(model.resources.headquarters, microFromString(result.headquarters));
    for (const id of ECONOMY_BUILDINGS) {
      transaction.set(modelBuildingResource(model, id), microFromString(result.buildings[id]));
      transaction.set(model.resources.wreckage[id], microFromString(result.wreckage[id]));
    }
    const logistics = transaction.getRecord(model.logistics) as LogisticsState;
    transaction.setRecord(
      model.logistics,
      cloneLogisticsState({
        ...logistics,
        borders: Object.fromEntries(
          Object.entries(logistics.borders).map(([id, border]) => {
            const resultBorder = result.borders[id] as (typeof result.borders)[string];
            return [id, { ...border, troops: resultBorder?.troops ?? border.troops }];
          }),
        ),
      }),
    );
    const combat = transaction.getRecord(model.combat);
    transaction.setRecord(
      model.combat,
      cloneCombatState({
        borders: Object.fromEntries(
          Object.entries(combat.borders).map(([id, border]) => {
            const resultBorder = result.borders[id] as (typeof result.borders)[string];
            if (!resultBorder) return [id, border];
            return [
              id,
              {
                ...border,
                shieldCharge: resultBorder.shieldCharge,
                effectiveTarget: resultBorder.effectiveTarget,
                lastAttack: resultBorder.lastAttack,
              },
            ];
          }),
        ),
      }),
    );
  }
  for (const breach of settled.breached)
    transaction.emit(shieldBreached, { seatId: breach.seatId, borderId: breach.borderId });
  return settled.reports;
}

function captureSeat(
  transaction: Transaction<Micro>,
  model: SeatModel,
  settlement: EconomySettlement,
  lifecycle: MatchLifecycleState,
  diplomacy: DiplomacyState,
  setup: LockedMatchSetup,
  elapsedMs: number,
): CombatSeatInput {
  const logistics = transaction.getRecord(model.logistics);
  const combat = transaction.getRecord(model.combat);
  const supplyCapacity = formatMicro(settlement.diagnostics.supplyCapacity);
  return Object.freeze({
    id: model.seat.id,
    headquarters: formatMicro(transaction.get(model.resources.headquarters)),
    buildings: Object.freeze(
      Object.fromEntries(
        ECONOMY_BUILDINGS.map((id) => [
          id,
          formatMicro(transaction.get(modelBuildingResource(model, id))),
        ]),
      ),
    ) as CombatSeatInput["buildings"],
    wreckage: Object.freeze(
      Object.fromEntries(
        ECONOMY_BUILDINGS.map((id) => [
          id,
          formatMicro(transaction.get(model.resources.wreckage[id])),
        ]),
      ),
    ) as CombatSeatInput["wreckage"],
    borders: Object.freeze(
      Object.fromEntries(
        model.borderIds
          .filter(
            (id) =>
              setup.teamBySeat[model.seat.id] !== setup.teamBySeat[id] &&
              !pactBlocksCombat(diplomacy, model.seat.id, id) &&
              borderCanFight(lifecycle, model.seat.id, id, transaction.gameTimeMs() - elapsedMs),
          )
          .map((id) => {
            const position = logistics.borders[id] as (typeof logistics.borders)[string];
            const defense = combat.borders[id] as (typeof combat.borders)[string];
            const allocation = Number(
              transaction.getAllocation(model.supplyAllocation.id, id) / 1_000_000n,
            );
            return [
              id,
              Object.freeze({
                troops: position.troops,
                standingOrder: position.standingOrder,
                shieldCharge: defense.shieldCharge,
                requestedTarget: defense.requestedTarget,
                supplyEffectiveness: supplyForPosition(position.troops, supplyCapacity, allocation)
                  .effectiveness,
              }),
            ];
          }),
      ),
    ),
    operatedShieldGenerators: formatMicro(settlement.diagnostics.operatedShieldGenerators),
    researchEffects: effectsForResearch(transaction.getRecord(model.research)),
  });
}
